/**
 * 通讯录可见规则设置（三类规则表格）
 */
import * as React from 'react';
import {
  AlertDialog,
  Avatar,
  Button,
  Description,
  toast,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { ContactVisibilityRulesModal } from './ContactVisibilityRulesModal';
import {
  CONTACT_VISIBILITY_RULE_SECTIONS,
  contactRuleSelectionKey,
  type ContactVisibilityRuleType,
} from './contact-visibility-utils';

type RuleRow = {
  id: string;
  ruleType: ContactVisibilityRuleType;
  targetType?: string | null;
  targetId?: string | null;
  label?: string | null;
  ruleDescription?: string | null;
  avatarUrl?: string | null;
  org?: { shortName?: string | null; fullName?: string | null } | null;
  person?: { fullName?: string | null; avatarUrl?: string | null } | null;
};

function resolveRuleLabel(rule: RuleRow): string {
  if (rule.targetType === 'ORG') {
    return rule.org?.shortName || rule.org?.fullName || rule.label || '—';
  }
  return rule.person?.fullName || rule.label || '—';
}

export interface ContactVisibilityRulesSettingsProps {
  appId: string;
  spaceId: string;
}

export function ContactVisibilityRulesSettings({
  appId,
  spaceId,
}: ContactVisibilityRulesSettingsProps) {
  const [modalRuleType, setModalRuleType] = React.useState<ContactVisibilityRuleType | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<RuleRow | null>(null);

  const utils = trpc.useUtils();

  const { data: rules, isLoading } = trpc.contactVisibilityRules.listAllWithLabels.useQuery({
    appId,
  });

  const deleteMutation = trpc.contactVisibilityRules.delete.useMutation({
    onSuccess: () => {
      toast.success('已删除规则');
      setDeleteTarget(null);
      void utils.contactVisibilityRules.listAllWithLabels.invalidate({ appId });
    },
    onError: (e) => toast.danger(e.message || '删除失败'),
  });

  const rulesByType = React.useMemo(() => {
    const map = new Map<ContactVisibilityRuleType, RuleRow[]>();
    for (const section of CONTACT_VISIBILITY_RULE_SECTIONS) {
      map.set(section.ruleType, []);
    }
    for (const rule of rules ?? []) {
      const ruleType = rule.ruleType as ContactVisibilityRuleType;
      const list = map.get(ruleType);
      if (list) {
        list.push(rule as RuleRow);
      }
    }
    return map;
  }, [rules]);

  const existingKeysByType = React.useMemo(() => {
    const map = new Map<ContactVisibilityRuleType, Set<string>>();
    for (const section of CONTACT_VISIBILITY_RULE_SECTIONS) {
      map.set(section.ruleType, new Set());
    }
    for (const rule of rules ?? []) {
      const ruleType = rule.ruleType as ContactVisibilityRuleType;
      const targetType = rule.targetType ?? 'ORG';
      const targetId = rule.targetId ?? rule.id;
      if (!targetId) continue;
      const key = contactRuleSelectionKey(ruleType, targetType, targetId);
      map.get(ruleType)?.add(key);
    }
    return map;
  }, [rules]);

  const invalidateRules = React.useCallback(() => {
    void utils.contactVisibilityRules.listAllWithLabels.invalidate({ appId });
  }, [appId, utils.contactVisibilityRules.listAllWithLabels]);

  return (
    <>
      <section className="flex shrink-0 flex-col gap-6">
        <div>
          <h3 className="text-base font-semibold">通讯录查看权限</h3>
          <Description className="text-muted-foreground mt-1 text-sm">
            配置隐藏的部门/成员，以及查看方的通讯录可见范围
          </Description>
        </div>

        {CONTACT_VISIBILITY_RULE_SECTIONS.map((section) => {
          const sectionRules = rulesByType.get(section.ruleType) ?? [];

          return (
            <div key={section.ruleType} className="flex flex-col gap-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-medium">{section.title}</h4>
                  <Description className="text-muted-foreground mt-0.5 text-sm">
                    {section.description}
                  </Description>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onPress={() => setModalRuleType(section.ruleType)}
                >
                  添加
                </Button>
              </div>

              {isLoading ? (
                <p className="text-muted-foreground text-sm">加载规则…</p>
              ) : sectionRules.length === 0 ? (
                <Description className="text-muted-foreground text-sm">暂无规则</Description>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <div
                    className="text-muted-foreground grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_4.5rem] gap-3 border-b border-border px-4 py-2 text-sm font-medium"
                    role="row"
                  >
                    <div role="columnheader">部门/成员</div>
                    <div role="columnheader">通讯录信息</div>
                    <div className="text-right" role="columnheader">
                      操作
                    </div>
                  </div>
                  <ul className="divide-y divide-border" aria-label={section.title}>
                    {sectionRules.map((rule) => {
                      const label = resolveRuleLabel(rule);
                      const avatarUrl = rule.avatarUrl ?? rule.person?.avatarUrl ?? null;

                      return (
                        <li
                          key={rule.id}
                          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_4.5rem] items-center gap-3 px-4 py-3"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <Avatar className="size-7 shrink-0" variant="soft">
                              {avatarUrl ? (
                                <Avatar.Image src={avatarUrl} alt={label} />
                              ) : null}
                              <Avatar.Fallback>{label.charAt(0).toUpperCase()}</Avatar.Fallback>
                            </Avatar>
                            <span className="truncate text-sm">{label}</span>
                          </div>
                          <div className="text-muted-foreground min-w-0 truncate text-sm">
                            {rule.ruleDescription || '—'}
                          </div>
                          <div className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-danger"
                              onPress={() => setDeleteTarget(rule)}
                            >
                              删除
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {modalRuleType ? (
        <ContactVisibilityRulesModal
          open
          ruleType={modalRuleType}
          appId={appId}
          spaceId={spaceId}
          existingKeys={existingKeysByType.get(modalRuleType) ?? new Set()}
          onOpenChange={(open) => {
            if (!open) setModalRuleType(null);
          }}
          onSuccess={invalidateRules}
        />
      ) : null}

      <AlertDialog isOpen={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定删除「{deleteTarget ? resolveRuleLabel(deleteTarget) : ''}」的可见规则吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteTarget(null)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending || !deleteTarget}
                  onPress={() => {
                    if (!deleteTarget) return;
                    deleteMutation.mutate({ id: deleteTarget.id });
                  }}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
